import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Edit, RefreshCw, Trash2, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD = { case_management: 'case_management_summary', success_coaching: 'success_coaching_summary' };

// Summary text with Edit / Regenerate / Delete for the Caseload Review summary dialogs
export const ManagedSummaryPanel = ({ kind, record, emptyText, boxClassName, canManage, onUpdated }) => {
  const field = FIELD[kind];
  const text = record?.[field];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const base = `${API}/caseload-review/records/${record?.id}/summary/${kind}`;

  const run = async (action, fn, message) => {
    setBusy(action);
    try {
      const { data } = await fn();
      onUpdated(data);
      toast.success(message);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (await run('edit', () => axios.put(base, { summary: draft }), 'Summary updated')) setEditing(false);
  };
  const regenerate = () => { setConfirm(null); run('regenerate', () => axios.post(`${base}/regenerate`), 'Summary regenerated'); };
  const remove = () => {
    setConfirm(null);
    run('delete', async () => {
      await axios.delete(base);
      return { data: { [field]: null, [`${field}_note_id`]: null, [`${field}_note_subject`]: null, [`${field}_generated_at`]: null } };
    }, 'Summary and its case note deleted');
  };

  return (
    <div className="space-y-3">
      {editing ? (
        <Textarea rows={8} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`${kind}-summary-edit-input`} />
      ) : (
        <div className={boxClassName}>
          <p className="text-sm whitespace-pre-wrap" data-testid={`${kind}-summary-text`}>{text || emptyText}</p>
        </div>
      )}
      {canManage && text && (
        <div className="flex flex-wrap gap-2" data-testid={`${kind}-summary-actions`}>
          {editing ? (
            <>
              <Button size="sm" onClick={save} disabled={busy === 'edit' || !draft.trim()} data-testid={`${kind}-summary-save-btn`}>
                {busy === 'edit' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={busy === 'edit'}>Cancel</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => { setDraft(text); setEditing(true); }} disabled={!!busy} data-testid={`${kind}-summary-edit-btn`}>
                <Edit className="w-3 h-3 mr-1" />Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirm('regenerate')} disabled={!!busy || !record?.[`${field}_note_id`]} data-testid={`${kind}-summary-regenerate-btn`}>
                {busy === 'regenerate' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}Regenerate
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirm('delete')} disabled={!!busy} data-testid={`${kind}-summary-delete-btn`}>
                {busy === 'delete' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}Delete
              </Button>
            </>
          )}
        </div>
      )}

      <AlertDialog open={confirm === 'regenerate'} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid={`${kind}-summary-regenerate-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate this summary?</AlertDialogTitle>
            <AlertDialogDescription>The AI will rebuild the summary from the case note it came from. This replaces the current summary text, including any edits.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={regenerate} data-testid={`${kind}-summary-regenerate-confirm-btn`}>Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirm === 'delete'} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="sm:max-w-xl" data-testid={`${kind}-summary-delete-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this summary?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting removes both the summary text and the case note it came from
              {record?.[`${field}_note_subject`] ? ` ("${record[`${field}_note_subject`]}")` : ''}. This cannot be undone.
              If you only want a fresh summary, you can regenerate it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-wrap gap-2 sm:space-x-0">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {record?.[`${field}_note_id`] && (
              <Button variant="outline" onClick={regenerate} data-testid={`${kind}-summary-regenerate-instead-btn`}>
                <RefreshCw className="w-3 h-3 mr-1" />Regenerate Instead
              </Button>
            )}
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid={`${kind}-summary-delete-confirm-btn`}>
              Delete Summary and Note
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
